import { useState, useEffect, useRef } from "react";
import { ToolCall, buildTerminalTheme } from "@fwf/fwfa-design-system/react";
import "@xterm/xterm/css/xterm.css";
import type { ActiveToolCall } from "@/types";
import { dsToolStatus, humanizeToolName } from "@/lib/tool-display";

interface Props {
  toolCall: ActiveToolCall;
}

export function ToolCallActivity({ toolCall }: Props) {
  const label = humanizeToolName(toolCall.toolName);
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<{
    write: (data: string) => void;
    reset: () => void;
    dispose: () => void;
  } | null>(null);
  const writtenOutputRef = useRef("");

  // Initialize xterm when expanded
  useEffect(() => {
    if (!expanded || !containerRef.current) return;

    let disposed = false;

    Promise.all([
      import("@xterm/xterm"),
      import("@xterm/addon-fit"),
    ]).then(([{ Terminal }, { FitAddon }]) => {
      if (disposed || !containerRef.current) return;

      const terminalTheme = buildTerminalTheme();

      const term = new Terminal({
        cursorBlink: false,
        disableStdin: true,
        fontSize: 11,
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        // Read-only pane, so the cursor is painted in the background colour to hide it.
        theme: { ...terminalTheme, cursor: terminalTheme.background },
        scrollback: 5000,
        convertEol: true,
      });

      const fitAddon = new FitAddon();
      term.loadAddon(fitAddon);
      term.open(containerRef.current);
      fitAddon.fit();

      if (toolCall.output) {
        term.write(toolCall.output);
        writtenOutputRef.current = toolCall.output;
      }

      termRef.current = term;
    });

    return () => {
      disposed = true;
      if (termRef.current) {
        termRef.current.dispose();
        termRef.current = null;
      }
      writtenOutputRef.current = "";
    };
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Replay output into xterm on each update.
  // ACP content updates are full replacements where earlier bytes can mutate
  // (e.g. a trailing \n becomes \x1b[2K\r for progress bar animation), so
  // we can't compute a safe delta. Reset and replay the full content instead.
  // xterm processes the ANSI sequences (cursor moves, line clears) internally
  // so the visual result is just the final frame, not a flicker of all frames.
  useEffect(() => {
    if (!termRef.current || !expanded) return;
    if (toolCall.output === writtenOutputRef.current) return;
    termRef.current.reset();
    termRef.current.write(toolCall.output);
    writtenOutputRef.current = toolCall.output;
  }, [toolCall.output, expanded]);

  // The DS owns the row, the glyphs, and the pane: one bordered box that clips
  // both children, so the row and the terminal can no longer come apart at the
  // seam. `pane` only appears once there is output to show, which is also what
  // makes the row a disclosure control.
  return (
    <ToolCall
      name={<span title={toolCall.toolName}>{label}</span>}
      status={dsToolStatus(toolCall.status)}
      pane={toolCall.output ? "terminal" : undefined}
      paneRef={containerRef}
      open={expanded}
      onOpenChange={setExpanded}
    />
  );
}
